import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateRandomId } from '../../utils/string';
import { Button, Form, Modal } from '@themesberg/react-bootstrap';
import { ErrorMessage } from '../scheme-management/Components/StyledComponents';
import EditableDropDown from '../../components/common/EditableDropdown';
import DatePicker from '../tripPayments/DatePicker';
import { returnVehiclePaymentModeOptions, transformLogs } from './vehicleUtils';
import { infoTable } from '../../utils/sms/infoTable';
import TableOnly from '../scheme-management/Components/IndividualScheme/TableOnly';

const VehiclePaymentsUpdateModal = (props) => {
  const { t } = useTranslation();
  const { showModal, closeModal, onConfirm, vehicle, setEditData } = props;
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'paymentMode' && value === 'perTrip'
        ? { paymentAmount: 0 }
        : {}),

      ...(field === 'ongoing' && value === true ? { toDate: null } : {}),
    }));
  };

  const onConfirmClick = () => {
    const errors = {};
    if (!vehicle.paymentMode) {
      errors.paymentMode = 'Please choose';
    }

    if (
      vehicle.paymentMode !== 'perTrip' &&
      (vehicle.paymentAmount === undefined ||
        vehicle.paymentAmount === '' ||
        isNaN(Number(vehicle.paymentAmount)))
    ) {
      errors.paymentAmount = 'Please enter a valid payment amount';
    }

    if (!vehicle.fromDate) {
      errors.fromDate = 'Please choose From Date';
    }

    if (!vehicle.ongoing && !vehicle.toDate) {
      errors.toDate = 'Please choose To Date';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      onConfirm(vehicle);
    }
  };

  return (
    <Modal
      centered
      id={generateRandomId()}
      size="lg"
      show={showModal}
      onHide={closeModal}
    >
      <Modal.Header>
        <Modal.Title className="h6">
          {t(`Update Payment Details for ${vehicle?.vehicleNumber}`)}
        </Modal.Title>
        <Button variant="close" aria-label="Close" onClick={closeModal} />
      </Modal.Header>
      <Modal.Body>
        <div className="mb-1">Last Payment Update:</div>
        {transformLogs(vehicle?.actionLog, 'EditPayment').length === 0 ? (
          <div className="text-center mb-3">
            <span className="text-muted">
              {t('No previous payment updates found for this vehicle.')}
            </span>
          </div>
        ) : (
          <TableOnly
            id="changeSummaryTable"
            data={transformLogs(vehicle?.actionLog, 'EditPayment')}
            columns={infoTable()}
            isFooterRequired={false}
          />
        )}
        <div className="d-flex mb-3 justify-content-between align-items-center gap-2 mt-4">
          <Form.Group className="w-50">
            <Form.Label>{t('Vehicle Number')}</Form.Label>
            <Form.Control
              value={vehicle.vehicleNumber}
              disabled={true}
              type="text"
            />
          </Form.Group>
          <Form.Group className="w-50">
            <Form.Label>{t('Vendor name')}</Form.Label>
            <Form.Control
              value={vehicle.vendorName}
              disabled={true}
              type="text"
            />
          </Form.Group>
        </div>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
          <Form.Group className="w-50">
            <Form.Label>{t('Payment Mode')}</Form.Label>
            <EditableDropDown
              width="100%"
              value={vehicle.paymentMode}
              onChange={(option) =>
                handleInputChange('paymentMode', option.value)
              }
              options={returnVehiclePaymentModeOptions()}
              placeholder={t('Choose Payment Mode')}
            />
            {!!formErrors['paymentMode'] && (
              <ErrorMessage>{t('Please choose')}</ErrorMessage>
            )}
          </Form.Group>
          <Form.Group className="w-50">
            <Form.Label>{t('Enter Payment Amount')}</Form.Label>
            <Form.Control
              value={vehicle.paymentAmount}
              isInvalid={formErrors['paymentAmount']?.length}
              type="number"
              onChange={(event) => {
                handleInputChange('paymentAmount', event.target.value);
              }}
              disabled={vehicle.paymentMode === 'perTrip'}
            />
            <Form.Control.Feedback type="invalid">
              {t(formErrors['paymentAmount'])}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="mt-3">
          <Form.Label>Select the Date</Form.Label>
        </div>
        <div className="d-flex justify-content-between align-items-start gap-2">
          {/* From Date */}
          <Form.Group className="w-100">
            <Form.Label>{t(`Update From`)}</Form.Label>
            <DatePicker
              value={vehicle.fromDate}
              setValue={(date) => {
                handleInputChange('fromDate', date);
              }}
              isInvalid={formErrors.fromDate}
            />
            {formErrors['fromDate'] && (
              <ErrorMessage>{t('Please choose From Date')}</ErrorMessage>
            )}
          </Form.Group>

          {/* To Date + Checkbox */}
          <Form.Group className="w-100 mb-3">
            <Form.Label>{t(`Update Till`)}</Form.Label>
            <DatePicker
              value={vehicle.toDate}
              setValue={(date) => {
                handleInputChange('toDate', date);
              }}
              isInvalid={formErrors.toDate}
              disabled={vehicle.ongoing}
            />
            {formErrors.toDate && (
              <ErrorMessage type="invalid">{t(formErrors.toDate)}</ErrorMessage>
            )}

            <Form.Check
              className="mt-2"
              type="checkbox"
              label={t(`Ongoing until Changed`)}
              checked={vehicle.ongoing}
              onChange={(event) => {
                handleInputChange('ongoing', event.target.checked);
              }}
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex">
          <Button
            style={{ backgroundColor: '#F0F0F0' }}
            className="mx-2 mt-3"
            variant="outline"
            onClick={() => closeModal()}
          >
            {t('Cancel')}
          </Button>
          <Button
            style={{ width: '98%' }}
            className="mx-2 mt-4"
            variant="primary"
            onClick={() => onConfirmClick()}
          >
            {t(`Confirm`)}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default VehiclePaymentsUpdateModal;
